import { useAuth0 } from '@auth0/auth0-react';
import { Avatar, Divider, Group, Popover, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight, IconCookie, IconLogout, IconSettings, IconUser } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores/userStore';
import { openAnalyticsSettings } from '../../services/analyticsService';
import classes from './UserAvatar.module.css';

interface UserAvatarProps {
  size?: number;
}

export default function UserAvatar({ size = 32 }: UserAvatarProps) {
  const { user: auth0User } = useAuth0();
  const { user } = useUserStore();
  const navigate = useNavigate();
  const [popoverOpened, setPopoverOpened] = useState(false);

  // Use internal username from user store, fallback to Auth0 user object
  const username = user?.username || auth0User?.name || auth0User?.nickname || auth0User?.email || 'Unknown User';

  // Extract avatar URL from the custom property in the JWT token
  const avatarUrl = auth0User?.['https://api.thegameproject.net/picture'] as string;

  const handleAccountSettings = () => {
    setPopoverOpened(false);
    navigate('/profile');
  };

  const handleLogout = () => {
    setPopoverOpened(false);
    navigate('/logout');
  };

  return (
    <Popover
      opened={popoverOpened}
      onChange={setPopoverOpened}
      position="bottom-end"
      shadow="xl"
      width={288}
      offset={10}
    >
      <Popover.Target>
        <UnstyledButton
          aria-label="Open account menu"
          aria-expanded={popoverOpened}
          onClick={() => setPopoverOpened((o) => !o)}
          className={classes.trigger}
          data-opened={popoverOpened || undefined}
        >
          <Avatar
            src={avatarUrl}
            size={size}
            radius="xl"
            alt="User avatar"
            imageProps={{ referrerPolicy: 'no-referrer' }}
          >
            <IconUser size={size * 0.6} />
          </Avatar>
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown p={0} className={classes.dropdown}>
        <div className={classes.accountHeader}>
          <Group gap="md" wrap="nowrap">
            <Avatar
              src={avatarUrl}
              size={48}
              radius="xl"
              alt="User avatar"
              imageProps={{ referrerPolicy: 'no-referrer' }}
              className={classes.headerAvatar}
            >
              <IconUser size={24} />
            </Avatar>
            <div className={classes.identity}>
              <Text size="sm" fw={700} c="white" truncate>
                {username}
              </Text>
              {auth0User?.email && (
                <Text size="xs" c="#b9c9df" truncate>
                  {auth0User.email}
                </Text>
              )}
            </div>
          </Group>
        </div>

        <Stack gap={4} p="sm">
          <UnstyledButton className={classes.menuItem} onClick={handleAccountSettings}>
            <span className={classes.itemIcon}>
              <IconSettings size={18} />
            </span>
            <span className={classes.itemCopy}>
              <Text size="sm" fw={650}>
                Account settings
              </Text>
              <Text size="xs" c="dimmed">
                Profile, preferences and security
              </Text>
            </span>
            <IconChevronRight size={16} className={classes.chevron} />
          </UnstyledButton>
          <Divider my={4} color="gray.2" />
          <UnstyledButton
            className={classes.menuItem}
            onClick={() => {
              setPopoverOpened(false);
              openAnalyticsSettings();
            }}
          >
            <span className={classes.itemIcon}>
              <IconCookie size={18} />
            </span>
            <span className={classes.itemCopy}>
              <Text size="sm" fw={650}>
                Cookie settings
              </Text>
              <Text size="xs" c="dimmed">
                Manage analytics consent
              </Text>
            </span>
          </UnstyledButton>
          <Divider my={4} color="gray.2" />
          <UnstyledButton className={`${classes.menuItem} ${classes.logoutItem}`} onClick={handleLogout}>
            <span className={classes.itemIcon}>
              <IconLogout size={18} />
            </span>
            <span className={classes.itemCopy}>
              <Text size="sm" fw={650}>
                Log out
              </Text>
              <Text size="xs" c="dimmed">
                Sign out of The Game Project
              </Text>
            </span>
          </UnstyledButton>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
